// Deutsche Vorgabeseiten für 404/410 (Kontrakt v3, T-29). Werden von
// TemplateLoader gerendert, wenn kein Marken-Template greift — entweder weil
// gar keins konfiguriert ist, weil dessen ErrorView/GoneView-Export fehlt
// oder keine Funktion ist, oder weil die Marken-Komponente zur Laufzeit wirft
// (siehe TemplateErrorBoundary).
import type { ErrorProps, GoneProps, LandingProps } from './contract';

/**
 * 404: Unter dieser Adresse existiert keine vCard. `error.urlKey` bleibt
 * erlaubt (siehe `NotFoundPayload`) — hier zeigen wir ihn bewusst nicht an,
 * die Adresse steht ohnehin schon in der Adresszeile. Verweist auf
 * `data.brand.notFoundRedirectUrl`, falls die Marke einen gesetzt hat
 * (T-27) — fehlt er, gibt es keinen Verweis, keinen Platzhalter.
 */
export function ClassicNotFound({ data }: ErrorProps) {
  const redirectUrl = data.brand?.notFoundRedirectUrl;
  return (
    <div className="classic-error-view classic-not-found">
      <p>Diese Visitenkarte gibt es nicht.</p>
      {redirectUrl && (
        <a href={redirectUrl} rel="noopener noreferrer">
          {redirectUrl}
        </a>
      )}
    </div>
  );
}

/**
 * 410: Die vCard wurde anonymisiert (Art. 17 DSGVO, unumkehrbar). Zeigt
 * NIEMALS einen Namen und NIEMALS die Adresse — der Slug einer anonymisierten
 * Person ist selbst ein Personenbezug (deshalb liegt in der Sperrliste auch
 * nur dessen Hash). `GonePayload` trägt deshalb bewusst keinen `urlKey`, und
 * `GoneProps` keinen `vcard`-Zweig — es gibt hier strukturell nichts
 * Personenbezogenes zu zeigen.
 */
// Signatur folgt bewusst GoneProps (Kontrakt-Symmetrie zu ErrorProps), auch
// wenn diese Vorgabeseite strukturell nichts aus den Props zeigen darf.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ClassicGone(props: GoneProps) {
  return (
    <div className="classic-error-view classic-gone">
      <p>Diese Visitenkarte ist nicht mehr verfügbar.</p>
    </div>
  );
}

/**
 * `landing`: heute erzeugt kein Aufrufer diese Ansicht — kein Marken-Bundle
 * bekommt eine `LandingView` zu Gesicht. Genau deshalb war `TemplateLoader`
 * bislang mit `null` „billig" geschlossen: eine weiße Seite, die als
 * Ladeproblem gemeldet würde statt als das, was sie ist. Diese Vorgabeseite
 * behauptet bewusst NICHTS, was sie nicht weiß — keine Fehlermeldung (es ist
 * kein Fehler), nur eine schlichte Startseiten-Vertretung mit dem
 * Markennamen aus `data.brand`, falls vorhanden.
 */
export function ClassicLanding({ data }: LandingProps) {
  const brandName = data.brand?.name;
  return (
    <div className="classic-error-view classic-landing">
      <p>{brandName ? `Willkommen bei ${brandName}.` : 'Willkommen.'}</p>
    </div>
  );
}
